import { useMutation } from "@tanstack/react-query";

import { getApplication } from "../api/applications";
import {
  applicationToCalculateInput,
  isApplicationValidForCalculate,
  postCalculatePreview,
  type CalculatePreviewResponse,
  type MachineCostMode,
  type RegionCode,
} from "../api/calculate";

export type EvaluateInput = {
  region: RegionCode;
  productId: string;
  machineCostMode: MachineCostMode;
  applicationIds: string[];
};

export type EvaluateResult = {
  response: CalculatePreviewResponse;
  /** Selected app ids dropped before sending (missing volume/batch/fasteners). */
  skippedIds: string[];
};

/**
 * Cost Model Tools "Evaluate" — fetches each selected application's full detail,
 * drops the ones that can't be costed, then posts the same `/v1/calculate`
 * preview the customer comparison uses. Mirrors the POC admin evaluator.
 */
export function useEvaluateCostModel() {
  return useMutation<EvaluateResult, Error, EvaluateInput>({
    mutationFn: async ({ region, productId, machineCostMode, applicationIds }) => {
      const details = await Promise.all(applicationIds.map((id) => getApplication(id)));
      const valid = details.filter(isApplicationValidForCalculate);
      const skippedIds = details
        .filter((d) => !isApplicationValidForCalculate(d))
        .map((d) => d.applicationId);

      const response = await postCalculatePreview({
        region,
        productId: productId.trim(),
        machineCostMode,
        applications: valid.map(applicationToCalculateInput),
      });

      return { response, skippedIds };
    },
  });
}
